// Shared pieces of the assets pages (spec/dashboard.md sections 9.7–9.8): the kinds, the size format, the kind icon,
// the group select with "New group…", the reference list with links, and the record's staged edit.
import { useAssetUrls, useBranchAssetCtx } from '../../api/assetUrls';
import { useState, type KeyboardEvent } from 'react';
import { Link, useParams } from 'react-router';
import { ApiError } from '../../api/client';
import { ops } from '../../api/operations';
import type { Asset, Json } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { useContent } from '../../content/ContentContext';
import { useGame } from '../../shell/GameContext';
import { routes } from '../../shell/routes';
import { AudioLines, Braces, ExternalLink, FileCode2, FileText, Image, LayoutList, type LucideIcon, Package, Settings, Square, Type } from '../../ui/icons';
import { Select, TextInput } from '../../ui/inputs';
import type { Workspace } from '../../workspace/Workspace';

/** spec/api-conventions.md section 13.2 */
export const ASSET_KINDS = ['image', 'ui_object', 'addressable', 'addressables', 'view', 'font', 'audio', 'json', 'other', 'javascript', 'editor_script'] as const;

/** The kind the API would default to from a content type (section 13.2). */
export function kindFromContentType(type: string): string {
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('audio/')) return 'audio';
  if (type.startsWith('font/')) return 'font';
  if (type === 'application/json') return 'json';
  return 'other';
}

/** `photo.final.png` → { name: 'photo.final', extension: 'png' } */
export function splitFileName(fileName: string): { name: string; extension: string | undefined } {
  const dot = fileName.lastIndexOf('.');
  if (dot <= 0) return { name: fileName, extension: undefined };
  return { name: fileName.slice(0, dot), extension: fileName.slice(dot + 1).toLowerCase() || undefined };
}

/** `1.6 MB` (binary units, one decimal from KB up). */
export function formatBytes(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(1)} ${units[i]}`;
}

const KIND_ICON: Record<string, LucideIcon> = {
  image: Image,
  ui_object: Square,
  addressable: Package,
  addressables: Package,
  view: LayoutList,
  font: Type,
  audio: AudioLines,
  json: Braces,
  javascript: FileCode2,
  editor_script: Settings,
  other: FileText,
};

export function KindIcon({ kind, large }: { kind: string; large?: boolean }) {
  const Icon = KIND_ICON[kind] ?? FileText;
  return (
    <span className={`thumb placeholder asset-kind-icon ${large ? 'asset-kind-icon-l' : ''}`} role="img" aria-label={kind} title={kind}>
      <Icon aria-hidden="true" />
    </span>
  );
}

/**
 * The image thumbnail through a signed URL (M7 slice 3: the public content path answers 404 until a pinned release lists
 * the asset), else the kind icon (also while the URL is being signed).
 */
export function AssetPreview({ asset, large }: { asset: Asset; large?: boolean }) {
  const image = asset.kind === 'image' && Boolean(asset.contentPath);
  const urls = useAssetUrls(useBranchAssetCtx(), image ? [asset.id] : []);
  const src = image ? urls[asset.id] : undefined;
  if (src) return <img className={large ? 'asset-preview-large' : 'thumb asset-preview'} src={src} alt={asset.name} loading="lazy" />;
  return <KindIcon kind={asset.kind} large={large} />;
}

/** `512×256 · ppu, border` — the dimensions when `meta.width` / `meta.height` exist, then the editor's other fields. */
export function metaSummary(meta: unknown): string {
  if (!meta || typeof meta !== 'object' || Array.isArray(meta)) return meta === null || meta === undefined ? '—' : JSON.stringify(meta);
  const m = meta as Record<string, unknown>;
  const hasDims = m.width !== undefined && m.height !== undefined;
  const rest = Object.keys(m).filter((k) => !(hasDims && (k === 'width' || k === 'height')));
  const parts = [hasDims ? `${String(m.width)}×${String(m.height)}` : null, rest.length ? rest.join(', ') : null].filter(Boolean);
  return parts.length ? parts.join(' · ') : '—';
}

/** Stages one field of the record (section 9.7: an `asset` draft, PATCH with If-Match on Save All). */
export function stageAsset(workspace: Workspace, server: Asset, field: 'name' | 'group' | 'description' | 'meta' | 'flags' | 'kind', value: unknown): void {
  workspace.edit('asset', server.id, field, value, { base: { version: Number(server.version), value: server as unknown as Json }, label: server.name, template: 'Assets' });
}

const NEW_GROUP = '__hue:new-group__';

/** The group select over the existing groups plus "New group…" (which turns into a text field). */
export function GroupSelect({ value, groups, onChange, onCancel, autoFocus, size, label, disabled }: { value: string | null; groups: string[]; onChange: (group: string | null) => void; onCancel?: () => void; autoFocus?: boolean; size?: 's' | 'm'; label: string; disabled?: boolean }) {
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState('');
  if (adding) {
    const finish = () => {
      const t = text.trim();
      setAdding(false);
      setText('');
      if (t) onChange(t);
      else onCancel?.();
    };
    return (
      <TextInput
        size={size}
        autoFocus
        value={text}
        placeholder="New group name"
        aria-label={label}
        onChange={(e) => setText(e.target.value)}
        onBlur={finish}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            finish();
          } else if (e.key === 'Escape') {
            e.stopPropagation();
            setAdding(false);
            setText('');
            onCancel?.();
          }
        }}
      />
    );
  }
  const options = value && !groups.includes(value) ? [...groups, value].sort() : groups;
  return (
    <Select
      size={size}
      autoFocus={autoFocus}
      value={value ?? ''}
      aria-label={label}
      disabled={disabled}
      onChange={(e) => {
        const v = e.target.value;
        if (v === NEW_GROUP) setAdding(true);
        else onChange(v === '' ? null : v);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onCancel?.();
        }
      }}
    >
      <option value="">(no group)</option>
      {options.map((g) => (
        <option key={g} value={g}>
          {g}
        </option>
      ))}
      <option value={NEW_GROUP}>New group…</option>
    </Select>
  );
}

/** A one-line inline cell editor: Enter or blur commits, Escape cancels. */
export function InlineText({ value, onCommit, onCancel, label }: { value: string; onCommit: (v: string) => void; onCancel: () => void; label: string }) {
  const [text, setText] = useState(value);
  const commit = () => (text === value ? onCancel() : onCommit(text));
  return (
    <TextInput
      size="s"
      autoFocus
      value={text}
      aria-label={label}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          commit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          onCancel();
        }
      }}
    />
  );
}

export interface AssetRef {
  documentId: string;
  template?: string | null;
  path: string;
}

/** The references of an asset (GET …/assets/{asset}/references), every page. */
export function useAssetReferenceLoader() {
  const { org, game } = useGame();
  const { branch = '' } = useParams();
  const { client } = useAuth();
  return (assetId: string): Promise<AssetRef[]> =>
    client.all((cursor) => client.data(ops.listAssetReferences, { path: { org, game: game.slug, branch, asset: assetId }, query: { limit: 200, cursor } })).then((items) => items.map((r) => ({ documentId: r.documentId, template: r.template, path: r.path })));
}

/** A `409` of DELETE …/assets/{asset}: `entityId` = `<documentId>.<path>`. */
export function refsFromConflict(e: ApiError): AssetRef[] {
  return (e.details ?? [])
    .map((d) => {
      const raw = (d as { entityId?: string | null }).entityId ?? d.field ?? '';
      const entity = (d as { entity?: string | null }).entity;
      const documentId = entity && entity.length >= 36 && raw.startsWith(entity) ? entity : raw.slice(0, 36);
      const path = raw.length > documentId.length + 1 ? raw.slice(documentId.length + 1) : '';
      return { documentId, path };
    })
    .filter((r) => r.documentId);
}

/** The reference rows with ↗ links to the documents (`?doc=`). */
export function ReferenceList({ refs, testId, empty = 'No document references this asset.' }: { refs: AssetRef[]; testId?: string; empty?: string }) {
  const { org, game } = useGame();
  const { branch = '' } = useParams();
  const catalog = useContent();
  if (refs.length === 0) return <p className="muted small" data-testid={testId}>{empty}</p>;
  return (
    <ul className="asset-refs" data-testid={testId}>
      {refs.map((r, i) => {
        const doc = catalog.resolve(r.documentId);
        const template = r.template ?? doc?.template ?? null;
        const label = doc ? catalog.label(doc) : r.documentId.slice(0, 8);
        return (
          <li key={`${r.documentId}:${r.path}:${i}`} className="asset-ref">
            <FileText className="asset-ref-icon" aria-hidden="true" />
            {template ? (
              <Link to={`${routes.documents(org, game.slug, branch, template)}?doc=${encodeURIComponent(r.documentId)}`} title={`Open ${label}`}>
                {label} <ExternalLink className="icon-s" aria-hidden="true" />
              </Link>
            ) : (
              <span>{label}</span>
            )}
            {template && <span className="muted small"> {template.split('.').pop()}</span>}
            {r.path && <code className="small"> {r.path}</code>}
          </li>
        );
      })}
    </ul>
  );
}

export function errorText(e: unknown): string {
  return e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e);
}

export function copyText(text: string, done: () => void): void {
  void navigator.clipboard?.writeText(text).then(done, () => undefined);
}
